// components/WaterLevelSensorHistory/WaterLevelSensorHistory.js

import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Button,
  TextField,
  Snackbar,
  Alert,
  AppBar,
  Toolbar,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DownloadIcon from '@mui/icons-material/Download';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { API_BASE_URL } from '../../../config/config';

dayjs.extend(utc);

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label, selectedDataSeries }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Paper 
        elevation={4} 
        sx={{ 
          padding: 1.5, 
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid #e0e0e0',
          borderRadius: 1.5
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
          {label}
        </Typography>
        {selectedDataSeries.includes('presentLevel') && data.presentLevel !== undefined && (
          <Box sx={{ marginTop: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 600 }}>
              Level: {data.presentLevel.toFixed(1)}%
            </Typography>
          </Box>
        )}
        {selectedDataSeries.includes('flowRate') && data.flowRate !== undefined && (
          <Box sx={{ marginTop: 0.25 }}>
            <Typography variant="caption" sx={{ color: '#f57c00', fontWeight: 600 }}>
              Flow: {data.flowRate.toFixed(1)}%
            </Typography>
          </Box>
        )}
        {selectedDataSeries.includes('presentLevel') && data.flow !== undefined && (
          <Box sx={{ marginTop: 0.25 }}>
            <Typography
              variant="caption"
              sx={{
                color:
                  data.flow === 'upward'
                    ? '#2e7d32'
                    : data.flow === 'downward'
                    ? '#c62828'
                    : '#666',
                fontWeight: 600
              }}
            >
              Direction: {data.flow ? data.flow.charAt(0).toUpperCase() + data.flow.slice(1) : 'N/A'}
            </Typography>
          </Box>
        )}
      </Paper>
    );
  }

  return null;
};

const WaterLevelSensorHistory = () => {
  const { deviceCode, sensorSubId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const chartRef = useRef(null);

  // State Variables
  const [sensorData, setSensorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDataSeries, setSelectedDataSeries] = useState(['presentLevel']);
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [error, setError] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [chartData, setChartData] = useState([]);
  const [numericalData, setNumericalData] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  /**
   * Function to Fetch Sensor Logs from API
   */
  const fetchSensorLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      // Format dates to DD-MM-YYYY as per API requirement
      const formattedFromDate = fromDate.format('DD-MM-YYYY');
      const formattedToDate = toDate.format('DD-MM-YYYY');

      const response = await axios.get(`${API_BASE_URL}api/sensor/logs`, {
        params: {
          deviceCode,
          sensorSubId,
          fromDate: formattedFromDate,
          toDate: formattedToDate,
        },
      });

      if (
        response.data &&
        Array.isArray(response.data.presentLevelLogs) &&
        Array.isArray(response.data.flowRateLogs)
      ) {
        setSensorData({
          presentLevelLogs: response.data.presentLevelLogs,
          flowRateLogs: response.data.flowRateLogs,
        });
      } else {
        setError('Invalid data format received from the server.');
      }
    } catch (err) {
      console.error('Error fetching sensor logs:', err);
      setError('Failed to fetch sensor logs. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Function to Merge presentLevelLogs and flowRateLogs Based on Timestamp
   */
  const prepareChartData = () => {
    if (!sensorData) return [];

    const { presentLevelLogs, flowRateLogs } = sensorData;

    // Create a map for presentLevelLogs based on timestamp
    const presentLevelMap = {};
    presentLevelLogs.forEach((log) => {
      const timestamp = dayjs(log.timestamp).toISOString();
      presentLevelMap[timestamp] = {
        presentLevel: parseFloat(log.value),
        flow: log.flow !== 'null' ? log.flow : null,
      };
    });

    // Create a map for flowRateLogs based on timestamp
    const flowRateMap = {};
    flowRateLogs.forEach((log) => {
      const timestamp = dayjs(log.timestamp).toISOString();
      flowRateMap[timestamp] = {
        flowRate: parseFloat(log.value),
      };
    });

    // Merge the two maps
    const combinedTimestamps = new Set([
      ...Object.keys(presentLevelMap),
      ...Object.keys(flowRateMap),
    ]);

    const combinedData = Array.from(combinedTimestamps)
      .map((timestamp) => {
        const plData = presentLevelMap[timestamp] || {};
        const frData = flowRateMap[timestamp] || {};
        return {
          originalTimestamp: dayjs(timestamp).toDate(),
          timestamp: dayjs(timestamp).format('MM-DD HH:mm'),
          presentLevel: plData.presentLevel,
          flow: plData.flow,
          flowRate: frData.flowRate,
        };
      })
      .sort((a, b) => new Date(a.originalTimestamp) - new Date(b.originalTimestamp));

    return combinedData;
  };

  /**
   * Function to Prepare Numerical Data Based on Selected Data Series
   */
  const prepareNumericalData = () => {
    return chartData.map((row) => {
      const newRow = { timestamp: row.timestamp };
      if (selectedDataSeries.includes('presentLevel')) {
        newRow.presentLevel =
          row.presentLevel !== undefined && row.presentLevel !== null
            ? row.presentLevel.toFixed(2)
            : '-';
        newRow.flow =
          row.flow === 'upward'
            ? '↑'
            : row.flow === 'downward'
            ? '↓'
            : 'N/A';
      }
      if (selectedDataSeries.includes('flowRate')) {
        newRow.flowRate =
          row.flowRate !== undefined && row.flowRate !== null
            ? row.flowRate.toFixed(2)
            : '-';
      }
      return newRow;
    });
  };

  /**
   * Update chartData and numericalData Whenever sensorData or selectedDataSeries Changes
   */
  useEffect(() => {
    if (sensorData) {
      const updatedChartData = prepareChartData();
      setChartData(updatedChartData);
      setNumericalData(prepareNumericalData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sensorData, selectedDataSeries]);

  /**
   * Fetch Sensor Logs on Component Mount and Whenever Dependencies Change
   */
  useEffect(() => {
    fetchSensorLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceCode, sensorSubId, fromDate, toDate]);

  /**
   * Handle Data Series Selection Change
   */
  const handleDataSeriesChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedDataSeries(
      // On autofill we get a stringified value
      typeof value === 'string' ? value.split(',') : value
    );
  };

  /**
   * Handle Resetting Date Filters to Default (Today's Date)
   */
  const handleResetDates = () => {
    setFromDate(dayjs());
    setToDate(dayjs());
  };

  /**
   * Handle Tab Change Between Graphical and Numerical Data
   */
  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  /**
   * Handle Downloading Chart as SVG
   */
  const handleDownloadChart = () => {
    if (chartRef.current) {
      const svgElement = chartRef.current.querySelector('svg');
      if (svgElement) {
        let source = new XMLSerializer().serializeToString(svgElement);

        // Add namespaces if missing
        if (!source.match(/^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/)) {
          source = source.replace(
            /^<svg/,
            '<svg xmlns="http://www.w3.org/2000/svg"'
          );
        }
        if (
          !source.match(
            /^<svg[^>]+"http:\/\/www\.w3\.org\/1999\/xlink"/
          )
        ) {
          source = source.replace(
            /^<svg/,
            '<svg xmlns:xlink="http://www.w3.org/1999/xlink"'
          );
        }

        // Add XML declaration
        const xmlHeader = '<?xml version="1.0" standalone="no"?>\r\n';
        source = xmlHeader + source;

        const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
        saveAs(blob, 'water_level_chart.svg');

        // Show success snackbar
        setSnackbar({
          open: true,
          message: 'Chart downloaded successfully!',
          severity: 'success',
        });
      } else {
        alert('Chart SVG not found.');
      }
    }
  };

  /**
   * Handle Downloading Numerical Data as CSV
   */
  const handleDownloadCSV = () => {
    if (numericalData.length === 0) {
      alert('No numerical data available to download.');
      return;
    }

    // Generate CSV headers based on selected data series
    const headers = ['Timestamp'];
    if (selectedDataSeries.includes('presentLevel')) {
      headers.push('Present Level (%)', 'Flow Direction');
    }
    if (selectedDataSeries.includes('flowRate')) {
      headers.push('Flow Rate (%)');
    }

    // Generate CSV rows
    const rows = numericalData.map((row) => {
      const rowData = [row.timestamp];
      if (selectedDataSeries.includes('presentLevel')) {
        rowData.push(row.presentLevel !== undefined ? row.presentLevel : '-');
        rowData.push(row.flow !== undefined ? row.flow : '-');
      }
      if (selectedDataSeries.includes('flowRate')) {
        rowData.push(row.flowRate !== undefined ? row.flowRate : '-');
      }
      return rowData.join(',');
    });

    // Combine headers and rows
    const csvContent = [headers.join(','), ...rows].join('\n');

    // Create a Blob from the CSV content
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    // Use file-saver to save the CSV
    saveAs(blob, 'numerical_data.csv');

    // Show success snackbar
    setSnackbar({
      open: true,
      message: 'Numerical data downloaded successfully!',
      severity: 'success',
    });
  };

  /**
   * Handle Closing of Snackbar
   */
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // Render Loader While Data is Being Fetched
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  // Render Error Message if Data Fetching Fails
  if (error) {
    return (
      <Box p={3}>
        <AppBar position="static" color="transparent" elevation={0}>
          <Toolbar>
            <Tooltip title="Back to Sensors">
              <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} aria-label="back">
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="h5" component="div" sx={{ flexGrow: 1, ml: 2 }}>
              Sensor History
            </Typography>
          </Toolbar>
        </AppBar>
        <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Render Message if No Data is Available
  if (!sensorData) {
    return (
      <Typography variant="h6" align="center" sx={{ marginTop: 4 }}>
        Sensor data not found.
      </Typography>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#fafafa', minHeight: '100vh', pb: 4 }}>
      {/* Enhanced Header with Gradient Background */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 3,
          mb: 4,
          borderRadius: '0 0 16px 16px',
        }}
      >
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} sm="auto">
            <Tooltip title="Back to Sensors">
              <IconButton 
                onClick={() => navigate(-1)} 
                sx={{ color: 'white', mb: 1 }}
                aria-label="back"
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              Sensor History Dashboard
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Real-time monitoring and historical data analysis
            </Typography>
          </Grid>
          <Grid item xs={12} sm="auto">
            <Tooltip title="View Error History">
              <Button
                variant="contained"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' }
                }}
                startIcon={<ErrorOutlineIcon />}
                onClick={() =>
                  navigate(
                    `/waterlevel/errorhistory/${deviceCode}/${sensorSubId}`,
                    { state: { deviceCode, sensorSubId } }
                  )
                }
              >
                Error History
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ px: 3 }}>
        {/* Sensor Information - Horizontal Display */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Box
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                p: 2.5,
                borderRadius: 2,
                textAlign: 'center',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)'
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.5 }}>
                DEVICE CODE
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {deviceCode}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box
              sx={{
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                p: 2.5,
                borderRadius: 2,
                textAlign: 'center',
                boxShadow: '0 4px 12px rgba(245, 87, 108, 0.2)'
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.5 }}>
                SENSOR ID
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {sensorSubId}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box
              sx={{
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                p: 2.5,
                borderRadius: 2,
                textAlign: 'center',
                boxShadow: '0 4px 12px rgba(79, 172, 254, 0.2)'
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.5 }}>
                DATA POINTS
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {chartData.length}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box
              sx={{
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                color: 'white',
                p: 2.5,
                borderRadius: 2,
                textAlign: 'center',
                boxShadow: '0 4px 12px rgba(250, 112, 154, 0.2)'
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.5 }}>
                SERIES
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {selectedDataSeries.length}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Tab Navigation - Alternative Design */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => handleTabChange(null, 0)}
              sx={{
                px: 3,
                py: 1,
                border: '2px solid',
                borderColor: tabIndex === 0 ? '#667eea' : '#e0e0e0',
                color: tabIndex === 0 ? '#667eea' : '#999',
                backgroundColor: tabIndex === 0 ? '#f3f4ff' : 'transparent',
                fontWeight: tabIndex === 0 ? 600 : 500,
                borderRadius: 1.5,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#667eea',
                  backgroundColor: '#f3f4ff'
                }
              }}
            >
              📊 Analytics Chart
            </Button>
            <Button
              onClick={() => handleTabChange(null, 1)}
              sx={{
                px: 3,
                py: 1,
                border: '2px solid',
                borderColor: tabIndex === 1 ? '#667eea' : '#e0e0e0',
                color: tabIndex === 1 ? '#667eea' : '#999',
                backgroundColor: tabIndex === 1 ? '#f3f4ff' : 'transparent',
                fontWeight: tabIndex === 1 ? 600 : 500,
                borderRadius: 1.5,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#667eea',
                  backgroundColor: '#f3f4ff'
                }
              }}
            >
              📋 Raw Data
            </Button>
          </Box>
        </Box>

      {/* Tab Panels */}
      {tabIndex === 0 && (
        <Box>
          {/* Compact Filter Controls */}
          <Box
            sx={{
              background: 'white',
              p: 2.5,
              borderRadius: 2,
              mb: 3,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              border: '1px solid #e8e8e8'
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#333' }}>
              ⚙️ Filter Options
            </Typography>
            <Grid container spacing={2} alignItems="flex-end">
              {/* Data Series Selection */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="data-series-label">Select Metrics</InputLabel>
                  <Select
                    labelId="data-series-label"
                    id="data-series-select"
                    multiple
                    value={selectedDataSeries}
                    onChange={handleDataSeriesChange}
                    input={<OutlinedInput label="Select Metrics" />}
                    renderValue={(selected) =>
                      selected
                        .map((key) =>
                          key === 'presentLevel'
                            ? 'Water Level'
                            : key === 'flowRate'
                            ? 'Flow Rate'
                            : key
                        )
                        .join(', ')
                    }
                  >
                    <MenuItem value="presentLevel">
                      <Checkbox checked={selectedDataSeries.includes('presentLevel')} />
                      <ListItemText primary="Water Level" />
                    </MenuItem>
                    <MenuItem value="flowRate">
                      <Checkbox checked={selectedDataSeries.includes('flowRate')} />
                      <ListItemText primary="Flow Rate" />
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Date Pickers */}
              <Grid item xs={12} sm={6} md={3}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Start Date"
                    value={fromDate}
                    onChange={(newValue) => setFromDate(newValue)}
                    maxDate={toDate}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="End Date"
                    value={toDate}
                    onChange={(newValue) => setToDate(newValue)}
                    minDate={fromDate}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Grid>

              {/* Reset Button */}
              <Grid item xs={12} md={2}>
                <Button
                  variant="outlined"
                  fullWidth
                  size="small"
                  onClick={handleResetDates}
                  sx={{
                    borderColor: '#ddd',
                    color: '#667eea',
                    '&:hover': { borderColor: '#667eea', backgroundColor: '#f3f4ff' }
                  }}
                >
                  Reset
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Download Chart Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
            <Tooltip title="Download chart as SVG">
              <Button
                variant="contained"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadChart}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)'
                  }
                }}
              >
                Download SVG
              </Button>
            </Tooltip>
          </Box>

          {/* Chart Container */}
          <Box
            ref={chartRef}
            sx={{
              background: 'white',
              p: 3,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              border: '1px solid #e8e8e8'
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#333' }}>
              📈 Sensor Performance Analysis
            </Typography>
            {chartData.length > 0 ? (
              <Box sx={{ width: '100%', height: 450, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 60 }}>
                    <defs>
                      <linearGradient id="colorLevel" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#667eea" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#667eea" stopOpacity={0.1} />
                      </linearGradient>
                      <linearGradient id="colorFlow" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f57c00" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#f57c00" stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="4 4" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="timestamp"
                      tick={{ fontSize: 11, fill: '#666' }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval="preserveStartEnd"
                    />
                    {selectedDataSeries.includes('presentLevel') && (
                      <YAxis
                        yAxisId="left"
                        domain={[0, 100]}
                        tick={{ fontSize: 11, fill: '#666' }}
                        label={{ value: 'Level (%)', angle: -90, position: 'insideLeft', offset: 10 }}
                      />
                    )}
                    {selectedDataSeries.includes('flowRate') && (
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 11, fill: '#666' }}
                        label={{ value: 'Flow Rate (%)', angle: 90, position: 'insideRight', offset: 10 }}
                      />
                    )}
                    <RechartsTooltip
                      content={<CustomTooltip selectedDataSeries={selectedDataSeries} />}
                      cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
                    />
                    <Legend verticalAlign="top" height={36} />
                    {selectedDataSeries.includes('presentLevel') && (
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="presentLevel"
                        name="Water Level (%)"
                        stroke="#667eea"
                        fillOpacity={1}
                        fill="url(#colorLevel)"
                        strokeWidth={2.5}
                        connectNulls
                      />
                    )}
                    {selectedDataSeries.includes('flowRate') && (
                      <Area
                        yAxisId="right"
                        type="monotone"
                        dataKey="flowRate"
                        name="Flow Rate (%)"
                        stroke="#f57c00"
                        fillOpacity={1}
                        fill="url(#colorFlow)"
                        strokeWidth={2.5}
                        connectNulls
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography color="textSecondary">
                  📊 No data available for the selected date range
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {tabIndex === 1 && (
        <Box>
          {/* Download Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
            <Tooltip title="Download data as CSV">
              <Button
                variant="contained"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadCSV}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)'
                  }
                }}
              >
                Download CSV
              </Button>
            </Tooltip>
          </Box>

          {/* Data Table */}
          <Box
            sx={{
              background: 'white',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              border: '1px solid #e8e8e8',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ p: 2.5, borderBottom: '1px solid #e8e8e8' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#333' }}>
                📋 Detailed Sensor Records
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {numericalData.length} records found
              </Typography>
            </Box>

            {numericalData.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: '#f5f5f5',
                        '& th': {
                          fontWeight: 700,
                          color: '#333',
                          fontSize: '0.875rem',
                          borderBottom: '2px solid #e0e0e0',
                          py: 1.5,
                          px: 2
                        }
                      }}
                    >
                      <TableCell>Timestamp</TableCell>
                      {selectedDataSeries.includes('presentLevel') && (
                        <>
                          <TableCell align="center">Water Level (%)</TableCell>
                          <TableCell align="center">Direction</TableCell>
                        </>
                      )}
                      {selectedDataSeries.includes('flowRate') && (
                        <TableCell align="center">Flow Rate (%)</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {numericalData.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                          '&:hover': {
                            backgroundColor: '#f0f4ff',
                            transition: 'background-color 0.2s ease'
                          },
                          borderBottom: '1px solid #e8e8e8',
                          '& td': {
                            py: 1.25,
                            px: 2,
                            fontSize: '0.875rem'
                          }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500, color: '#333' }}>
                          {row.timestamp}
                        </TableCell>
                        {selectedDataSeries.includes('presentLevel') && (
                          <>
                            <TableCell align="center" sx={{ color: '#667eea', fontWeight: 600 }}>
                              {row.presentLevel !== undefined ? row.presentLevel : '-'}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: 600,
                                color:
                                  row.flow === '↑'
                                    ? '#2e7d32'
                                    : row.flow === '↓'
                                    ? '#c62828'
                                    : '#999'
                              }}
                            >
                              {row.flow}
                            </TableCell>
                          </>
                        )}
                        {selectedDataSeries.includes('flowRate') && (
                          <TableCell align="center" sx={{ color: '#f57c00', fontWeight: 600 }}>
                            {row.flowRate !== undefined ? row.flowRate : '-'}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography color="textSecondary">
                  📊 No numerical data available for the selected criteria
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
      </Box>

      {/* Snackbar for User Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%', fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Define PropTypes for type checking (if needed)
WaterLevelSensorHistory.propTypes = {
  // Define if any props are expected
};

export default WaterLevelSensorHistory;
